import Alert from '@mui/material/Alert';
import LockIcon from '@mui/icons-material/Lock';
import { debounce } from 'lodash';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NotificationSystem from 'react-notification-system';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import GlobalStyle from '../../GlobalStyle';
import Header from '../../folders/ToolBar';
import { uploadToBlobStorage } from '../../folders/subtitles/BlobVttFile';
import Footer from '../../folders/subtitles/Footer';
import Loading from '../../folders/subtitles/Loading';
import Player from '../../folders/subtitles/Player';
import ProgressBar from '../../folders/subtitles/ProgressBar';
import Subtitles from '../../folders/subtitles/Subtitles';
import Sub from '../../libs/Sub';
import { getSubNodesfromVtt, sub2vtt } from '../../libs/readSub/SubUtils';
import EditSessionService, { generateReadOnlyKey, generateSessionKey } from '../../services/EditSessionService';
import EditTrackingService from '../../services/EditTrackingService';
import logCollectionService from '../../services/LogCollectionService';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import { getKeyCode } from '../../utils';
import AppConfig from '../../utils/AppConfig';
import { LanguageList } from '../../utils/LanguageNames';
import { readSubtitleFromStorage, writeSubtitleToStorage } from '../../utils/subtitleStorage';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { fetchBlobUrl } from './ReadBlobToString';

const Style = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100%;
    overflow: hidden;

    .container {
        display: flex;
        flex: 1;
        flex-direction: column;
        overflow: hidden;

        @media (min-width: 768px) {
            flex-direction: row;
        }
    }

    .player-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
        min-height: 200px;
        padding: 10px;

        @media (min-width: 768px) {
            flex: 1;
            min-height: auto;
            max-height: calc(100vh - 360px); /* Adjusted for taller footer */
        }
    }

    .subtitles-container {
        flex: 1;
        width: 100%;
        overflow: auto;

        @media (min-width: 768px) {
            max-height: calc(100vh - 360px); /* Adjusted for taller footer */
        }
    }

    .footer {
        height: auto;
        min-height: 200px; /* Increased from 100px */
        max-height: 200px; /* Increased from 200px */
        margin-bottom: 18px; /* Increased from 10px */
    }

    /* Add styles for the read-only banner */
    .read-only-banner {
        position: sticky;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        width: 100%;
    }

    /* Style for inactivity warning banner */
    .inactivity-warning-banner {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1100;
        width: 100%;
        animation: pulse 1s infinite alternate;
    }

    @keyframes pulse {
        from {
            opacity: 0.8;
        }
        to {
            opacity: 1;
        }
    }

    .toolbar-container {
        display: flex;
        align-items: center;
        padding: 5px;
        background-color: rgba(30, 30, 30, 0.95);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        overflow-x: auto;

        @media (min-width: 768px) {
            padding: 5px 10px;
        }
    }

    .toolbar-group {
        display: flex;
        align-items: center;
        margin-right: 8px;
        padding-right: 8px;
        border-right: 1px solid rgba(255, 255, 255, 0.1);
        white-space: nowrap;

        &:last-child {
            border-right: none;
        }

        @media (min-width: 768px) {
            margin-right: 12px;
            padding-right: 12px;
        }
    }

    .toolbar-spacer {
        flex: 1;
    }
`;

export default function VideoPlayerApp({ defaultLang }) {
    useTrackUsage('SubtitleEditor', 'editor', 'subtitles');
    const subtitleHistory = useRef([]);
    const notificationSystem = useRef(null);
    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState('');
    const [processing, setProcessing] = useState(0);
    const [language, setLanguage] = useState(defaultLang);
    const [subtitle, setSubtitleOriginal] = useState([]);
    const [waveform, setWaveform] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [translate, setTranslate] = useState('en');
    const [directUrl, setDirectUrl] = useState(null);
    const [viewEng, setViewEng] = useState(false);
    const [bookmarked, setBookmarked] = useState([]);
    const [subtitleComment, setSubtitleComment] = useState([]);
    const broadcastChannelRef = useRef(null);

    // Opened from a task: start on the task's language (same as SubtitleEditorV2)
    const [selectedLng, setSelectedLng] = useState(() => {
        const lang = window.history.state?.usr?.selectedLanguage;
        if (!lang) return 'English';
        try {
            const vp = JSON.parse(localStorage.getItem('videoProps'));
            if (vp?.videoLanguages?.some((l) => l.languageName === lang)) return lang;
        } catch (_) {}
        return 'English';
    });
    const [lngIndex, setLngIndex] = useState('0');

    // Search related state
    const [searchTerm, setSearchTerm] = useState('');
    const [replaceTerm, setReplaceTerm] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [matchOffsets, setMatchOffsets] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

    const newSub = useCallback((item) => new Sub(item), []);
    // Replace the current hasSub implementation with this more robust version
    const hasSub = useCallback(
        (sub) => {
            // Find subtitle by matching start and end times instead of object reference
            return subtitle.findIndex((item) => item.start === sub.start && item.end === sub.end);
        },
        [subtitle],
    );
    // Add state for tracking edit session
    const [readOnly, setReadOnly] = useState(false);
    const [activeEditor, setActiveEditor] = useState(null);
    // Add this with your other refs at the top of the component
    const keepAliveIntervalRef = useRef(null);
    // Add navigation hook
    const navigate = useNavigate();
    const [duplicateSessionDetected, setDuplicateSessionDetected] = useState(false);
    // Add state and ref for inactivity tracking
    const [lastActivity, setLastActivity] = useState(Date.now());
    const inactivityTimeoutRef = useRef(null);
    const INACTIVITY_TIMEOUT = AppConfig.sessionInactivityTimeoutMinutes * 60 * 1000;
    // Add these near your other state declarations
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    // Add these states
    const [sessionActive, setSessionActive] = useState(false);
    const [userLevel, setUserLevel] = useState('');
    const [mirroredGujaratiSubs, setMirroredGujaratiSubs] = useState([]);

    // Add these state variables after your existing state declarations (around line 165)
    const [leftColumnLanguage, setLeftColumnLanguage] = useState('English');
    const [rightColumnLanguage, setRightColumnLanguage] = useState('Gujarati');
    const [showDualColumns, setShowDualColumns] = useState(false);

    // Always-current ref for selectedLng — updated synchronously during render.
    // Used by setSubtitle / setLanguageSubTitle so that any stale callback (e.g. one
    // captured inside an async mergeSubtitleWithEnglish before a language switch) still
    // writes to the CORRECT per-language storage key instead of the old one.
    const selectedLngRef = useRef(selectedLng);
    selectedLngRef.current = selectedLng;

    // Add this useEffect to handle window resizing
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }, 200);

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Fetch mirrored Gujarati VTT once when English is loaded
    useEffect(() => {
        const fetchMirroredGujarati = async () => {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            if (selectedLng === 'English') {
                const gujratiVTTUrl = await getLanguageVTTFile('Gujarati');
                const vttData = await fetchBlobUrl(gujratiVTTUrl);
                if (vttData) {
                    setMirroredGujaratiSubs(getSubNodesfromVtt(vttData));
                } else {
                    setMirroredGujaratiSubs([]);
                }
            }
        };
        fetchMirroredGujarati();
    }, [selectedLng]);

    // Update mirroredGujaratiSubs timings when English subtitle timings change
    const updateMirroredGujaratiTimings = useCallback((newEnglishSubs) => {
        setMirroredGujaratiSubs((prevGujaratiSubs) => {
            // Map timings from English, keep Gujarati text, add blank if new
            return newEnglishSubs.map((eng, i) => {
                const guj = prevGujaratiSubs[i];
                return {
                    ...((guj && { ...guj }) || {}),
                    start: eng.start,
                    end: eng.end,
                    Estart: eng.Estart,
                    Eend: eng.Eend,
                    // If new cue, blank text
                    text: guj ? guj.text : '',
                    text2: guj ? guj.text2 : '',
                    note: guj ? guj.note : '',
                    bookmark: guj ? guj.bookmark : false,
                };
            });
        });
    }, []);

    // On auto/manual save, save mirroredGujaratiSubs to mirroredTimestampVtt
    const saveMirroredGujaratiVtt = useCallback(
        async (saveType = 'manual') => {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            if (videoProps?.mirroredTimestampVtt && mirroredGujaratiSubs.length > 0) {
                const vttContent = sub2vtt(mirroredGujaratiSubs, false);
                const gujaratiVttUrl = await getLanguageVTTFile('Gujarati');
                await uploadToBlobStorage(gujaratiVttUrl, vttContent, 'vtt');
                await videoCRUDServiceInstance.saveMetaDataforVideoId(videoProps.id, [], [], 'Gujarati', saveType);
            }
        },
        [mirroredGujaratiSubs],
    );

    const splitMirroredGujratiSubtitle = useCallback((index) => {
        setMirroredGujaratiSubs((prevSubs) => {
            if (!prevSubs || prevSubs.length === 0 || index < 0 || index >= prevSubs.length) return prevSubs;

            // Insert an empty text at index + 1, shift the rest down
            const newSubs = [
                ...prevSubs.slice(0, index + 1),
                {
                    ...prevSubs[index + 1],
                    text: '',
                    text2: '',
                    note: '',
                    bookmark: false,
                },
                ...prevSubs.slice(index + 1),
            ];

            return newSubs;
        });
    }, []);

    const addMirroredGujaratiSub = useCallback((index, newSub) => {
        setMirroredGujaratiSubs((prevSubs) => {
            // Insert a new sub at index with empty text fields, shift text fields down
            const newMirrored = {
                ...newSub,
                text: '',
                text2: '',
                note: '',
                bookmark: false,
            };
            return [
                ...prevSubs.slice(0, index),
                newMirrored,
                ...prevSubs.slice(index).map((sub) => ({
                    ...sub,
                    text: sub.text,
                    text2: sub.text2,
                    note: sub.note,
                    bookmark: sub.bookmark,
                })),
            ];
        });
    }, []);

    // Debounce the activity handler to prevent too many state updates
    const handleUserActivity = useCallback(
        debounce(() => {
            setLastActivity(Date.now());
        }, 500),
        [],
    );

    // Function to start edit session
    const startEditSession = useCallback(async (videoId, languageName) => {
        // Reset activity timer whenever starting an edit session
        handleUserActivity();
        logCollectionService.logTaggedEvent(
            'subtitle_session_start_requested',
            { videoId, languageName },
            ['session', 'subtitle-editor'],
        );

        console.log('Starting edit session for', videoId, languageName);
        console.log(
            'sessionStorage contents:',
            Object.keys(sessionStorage).reduce((obj, key) => {
                obj[key] = sessionStorage.getItem(key);
                return obj;
            }, {}),
        );

        try {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));
            const videoProps = JSON.parse(localStorage.getItem('videoProps')); // Add this line

            // Clean up any stale read-only flags first - add this line
            const readOnlyKey = `read_only_${videoId}_${languageName}`;
            sessionStorage.removeItem(readOnlyKey);

            // First, check if there's already a session in another tab
            const existingSession = await EditSessionService.checkForExistingSession(videoId, languageName);
            if (existingSession.exists) {
                logCollectionService.logTaggedEvent(
                    'subtitle_duplicate_session_detected',
                    { videoId, languageName },
                    ['session', 'subtitle-editor', 'duplicate-session'],
                );
                // Found existing session in another tab - go into read-only mode
                setReadOnly(true);
                setActiveEditor(authUser.email); // It's this user in another tab
                setDuplicateSessionDetected(true);

                // Mark this as a read-only session
                EditSessionService.markSessionAsReadOnly(videoId, languageName, existingSession.sessionId);

                Swal.fire({
                    title: 'Already Open',
                    html: `
                        <p>This file is already open in another tab or window. Please close the other instance to edit here.</p>
                        <p style="margin-top: 15px; font-size: 14px;">If you believe this is an error, please:</p>
                        <ol style="text-align: left; margin-top: 5px; font-size: 14px;">
                            <li>Go to <a href="/sessions/active" target="_blank" style="font-weight: bold; color: #3085d6;">Sessions Manager (It will open in another tab)</a></li>
                            <li>Close this window/tab or go back to the folder view </li>
                            <li>Review and delete any stale sessions in the Active Editing Sessions Page </li>
                            <li>Try opening this file again</li>
                        </ol>
                    `,
                    icon: 'warning',
                    confirmButtonText: 'OK',
                });
                return;
            }

            // Check if we're in read-only mode from a previous detection
            if (EditSessionService.isReadOnlySession(videoId, languageName)) {
                setReadOnly(true);
                setDuplicateSessionDetected(true);
                return;
            }

            // No existing session in another tab, proceed with normal flow
            const result = await EditSessionService.startEditSession(videoId, languageName, authUser.email, authToken, 'V1');

            // Set up broadcast channel to listen for future tab opens
            setupBroadcastListener(videoId, languageName);

            if (result.status === 'SUCCESS' || result.status === 'RECONNECTED') {
                logCollectionService.logTaggedEvent(
                    'subtitle_session_started',
                    { videoId, languageName, status: result.status },
                    ['session', 'subtitle-editor'],
                );
                setReadOnly(false);
                setActiveEditor(authUser.email);

                // Clear any existing interval
                if (keepAliveIntervalRef.current) {
                    clearInterval(keepAliveIntervalRef.current);
                    keepAliveIntervalRef.current = null;
                }

                // Create new interval
                console.log(`Setting new keep-alive interval for ${languageName}`);
                keepAliveIntervalRef.current = setInterval(() => {
                    keepAliveEditSession(videoProps.id, languageName);
                }, 60000); // Every minute
            } else if (result.status === 'DUPLICATE_SESSION') {
                logCollectionService.logTaggedEvent(
                    'subtitle_session_rejected_duplicate',
                    { videoId, languageName },
                    ['session', 'subtitle-editor', 'duplicate-session'],
                );
                setReadOnly(true);
                setActiveEditor(result.activeEditor);
                setDuplicateSessionDetected(true);

                // Mark this as a read-only session
                EditSessionService.markSessionAsReadOnly(videoId, languageName, result.sessionId);

                // Show notification that this file is already open in another tab
                Swal.fire({
                    title: 'Already Open',
                    html: `
                        <p>This file is already open in another tab or window. Please close the other instance to edit here.</p>
                        <p style="margin-top: 15px; font-size: 14px;">If you believe this is an error, please:</p>
                        <ol style="text-align: left; margin-top: 5px; font-size: 14px;">
                            <li>Go to <a href="/sessions/active" target="_blank" style="font-weight: bold; color: #3085d6;">Sessions Manager (It will open in another tab)</a></li>
                            <li>Close this window/tab or go back to the folder view </li>
                            <li>Review and delete any stale sessions in the Active Editing Sessions Page </li>
                            <li>Try opening this file again</li>
                        </ol>
                    `,
                    icon: 'warning',
                    confirmButtonText: 'OK',
                });
            } else if (result.status === 'LOCKED') {
                logCollectionService.logTaggedEvent(
                    'subtitle_session_locked',
                    { videoId, languageName, activeEditor: result.activeEditor },
                    ['session', 'subtitle-editor', 'read-only'],
                );
                setReadOnly(true);
                setActiveEditor(result.activeEditor);

                // Show notification that someone else is editing
                Swal.fire({
                    title: 'Read-Only Mode',
                    text: `This subtitle is currently being edited by ${result.activeEditor}. You can view it, but cannot make changes.`,
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
            }
        } catch (error) {
            console.error('Error starting edit session:', error);
            logCollectionService.logTaggedEvent(
                'subtitle_session_start_failed',
                {
                    videoId,
                    languageName,
                    message: error?.message,
                },
                ['session', 'subtitle-editor', 'error'],
            );
            setReadOnly(true);
            setActiveEditor('The editing session could not be established. Your changes will not be saved. Please exit the video and try reopening it after a moment.');
        }
    });

    // Set up a broadcast channel listener to detect if the file is opened in another tab
    const setupBroadcastListener = useCallback((videoId, languageName) => {
        // Close any existing channel first
        if (broadcastChannelRef.current) {
            broadcastChannelRef.current.close();
            broadcastChannelRef.current = null;
        }

        const channel = new BroadcastChannel(`amrutvaani-session-check-${videoId}-${languageName}`);
        broadcastChannelRef.current = channel;

        channel.onmessage = (event) => {
            if (event.data.checkSession) {
                const sessionKey = generateSessionKey(videoId, languageName);
                const sessionId = sessionStorage.getItem(sessionKey);
                //console.log('Session ID:', sessionId);
                if (sessionId) {
                    channel.postMessage({
                        hasSession: true,
                        sessionId: sessionId,
                    });
                }
            }
        };

        return () => {
            if (broadcastChannelRef.current) {
                broadcastChannelRef.current.close();
                broadcastChannelRef.current = null;
            }
        };
    });

    // Function for keep-alive
    const keepAliveEditSession = useCallback(async (videoId, languageName) => {
        if (duplicateSessionDetected) return; // Don't send keep-alive for duplicate sessions
        console.log('Keeping edit session alive for', videoId, languageName);
        try {
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));

            await EditSessionService.keepAliveEditSession(videoId, languageName, authUser.email, authToken);
        } catch (error) {
            console.error('Error keeping edit session alive:', error);
            logCollectionService.logTaggedEvent(
                'subtitle_session_keepalive_failed',
                {
                    videoId,
                    languageName,
                    message: error?.message,
                },
                ['session', 'subtitle-editor', 'error'],
            );
        }
    });

    // Create a helper function for session cleanup
    const cleanupSessionStorage = useCallback((videoId, language) => {
        if (videoId && language) {
            const sessionKey = generateSessionKey(videoId, language);
            const readOnlyKey = generateReadOnlyKey(videoId, language);
            sessionStorage.removeItem(sessionKey);
            sessionStorage.removeItem(readOnlyKey);
            console.log('Cleaned up session storage for', videoId, language, sessionKey, readOnlyKey);
            console.log(
                'sessionStorage contents:',
                Object.keys(sessionStorage).reduce((obj, key) => {
                    obj[key] = sessionStorage.getItem(key);
                    return obj;
                }, {}),
            );
        }

        // Clear interval
        if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
        }
    }, []);

    // Function to end edit session
    const endEditSession = useCallback(
        async (isUnloading = false) => {
            try {
                // End tracking session
                if (sessionActive) {
                    await EditTrackingService.endSession();
                    setSessionActive(false);
                }

                const videoProps = JSON.parse(localStorage.getItem('videoProps'));
                const authUser = JSON.parse(localStorage.getItem('authUser'));
                const authToken = JSON.parse(localStorage.getItem('authToken'));

                if (videoProps?.id && selectedLng) {
                    const previousLanguage = previousLanguageRef.current;
                    // If this is an active editing session, call the API
                    if (
                        !duplicateSessionDetected &&
                        !readOnly &&
                        !EditSessionService.isReadOnlySession(videoProps.id, selectedLng)
                    ) {
                        // For active editing sessions, call the API
                        if (isUnloading) {
                            // Use sendBeacon for more reliable cleanup during page unload
                            await EditSessionService.endEditSessionWithBeacon(videoProps.id, selectedLng);

                            // Also clean for the previous language if it exists

                            if (previousLanguage && previousLanguage !== selectedLng) {
                                await EditSessionService.endEditSessionWithBeacon(videoProps.id, previousLanguage);
                            }
                        } else {
                            // Regular async API call for normal navigation
                            await EditSessionService.endEditSession(
                                videoProps.id,
                                selectedLng,
                                authUser.email,
                                authToken,
                            );
                        }
                    }

                    cleanupSessionStorage(videoProps.id, selectedLng);
                    if (isUnloading) {
                        cleanupSessionStorage(videoProps.id, previousLanguage);
                    }

                    // Always clear interval
                    if (keepAliveIntervalRef.current) {
                        clearInterval(keepAliveIntervalRef.current);
                        keepAliveIntervalRef.current = null;
                    }
                }
            } catch (error) {
                console.error('Error ending edit session:', error);
                // Still clean up locally even if API fails
                try {
                    const videoProps = JSON.parse(localStorage.getItem('videoProps'));
                    if (videoProps?.id && selectedLng) {
                        cleanupSessionStorage(videoProps.id, selectedLng);
                    }
                } catch (e) {
                    console.error('Error cleaning up session storage:', e);
                }
            }
        },
        [duplicateSessionDetected, readOnly, selectedLng, cleanupSessionStorage, sessionActive],
    );

    // Add this effect with no dependencies
    useEffect(() => {
        return () => {
            // This runs when the component unmounts for any reason
            console.log('SubtitleEditor component unmounting');
            endEditSession(true);
            if (broadcastChannelRef.current) {
                broadcastChannelRef.current.close();
                broadcastChannelRef.current = null;
            }
        };
    }, [endEditSession]);

    // Set up inactivity tracking
    useEffect(() => {
        // This effect sets up the inactivity tracking timer
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];

        // Add event listeners for all activity events
        activityEvents.forEach((event) => {
            document.addEventListener(event, handleUserActivity);
        });

        // Create a function to check for inactivity
        const checkInactivity = () => {
            const timeNow = Date.now();
            const timeSinceLastActivity = timeNow - lastActivity;

            if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
                logCollectionService.logTaggedEvent(
                    'subtitle_session_inactive_timeout',
                    {
                        timeSinceLastActivity,
                        threshold: INACTIVITY_TIMEOUT,
                    },
                    ['session', 'inactivity', 'subtitle-editor'],
                );
                // Log the inactivity for debugging
                console.log(`Inactive for ${Math.round(timeSinceLastActivity / 60000)} minutes, navigating to home`);

                // First end the edit session
                endEditSession(true);

                // Show a notification to the user
                Swal.fire({
                    title: 'Session Expired',
                    text: 'You have been inactive for 15 minutes. Your session has been closed.',
                    icon: 'info',
                    timer: 5000,
                    timerProgressBar: true,
                }).then(() => {
                    // Navigate to home page
                    navigate('/');
                });

                // Clear the interval to prevent further checks
                if (inactivityTimeoutRef.current) {
                    clearInterval(inactivityTimeoutRef.current);
                }
            }
        };

        // Start the inactivity checker interval
        inactivityTimeoutRef.current = setInterval(checkInactivity, 60000); // Check every minute

        // Clean up event listeners and interval on unmount
        return () => {
            activityEvents.forEach((event) => {
                document.removeEventListener(event, handleUserActivity);
            });

            if (inactivityTimeoutRef.current) {
                clearInterval(inactivityTimeoutRef.current);
                inactivityTimeoutRef.current = null;
            }
        };
    }, [lastActivity, handleUserActivity, navigate, endEditSession]);

    // Clean up previous sessions for any language
    const cleanupAllSessions = useCallback(() => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        if (videoProps && videoProps.id) {
            // Clear any session storage for the current video
            Object.keys(sessionStorage).forEach((key) => {
                if (key.startsWith(`edit_session_${videoProps.id}_`) || key.startsWith(`read_only_${videoProps.id}_`)) {
                    console.log('Cleaning up session storage key:', key);
                    sessionStorage.removeItem(key);
                }
            });
        }
    }, []);

    // Add this with your other refs near the top of the component
    const previousLanguageRef = useRef(null);
    const inMiddleofSessionEnding = useRef(false);

    const formatSub = useCallback(
        (sub) => {
            if (Array.isArray(sub)) {
                return sub.map((item) => newSub(item));
            }
            return newSub(sub);
        },
        [newSub],
    );

    const setLanguageSubTitle = useCallback(
        (newSubtitle, saveToHistory = true) => {
            if (!isEqual(newSubtitle, subtitle)) {
                if (saveToHistory) {
                    subtitleHistory.current = [];
                    //subtitleHistory.current.push(formatSub(subtitle));
                }
                // Use selectedLngRef.current (not the closed-over selectedLng) so that any
                // stale callback (e.g. from an async mergeSubtitleWithEnglish captured before
                // a language switch) still writes to the CORRECT per-language key.
                writeSubtitleToStorage(newSubtitle, selectedLngRef.current);

                setSubtitleOriginal(formatSub(newSubtitle));
                window.dispatchEvent(new Event('storage'));
            }
        },
        [subtitle, setSubtitleOriginal, formatSub],
    );

    const setSubtitle = useCallback(
        (newSubtitle, saveToHistory = true, skipUpdateMirrored = false) => {
            if (!isEqual(newSubtitle, subtitle)) {
                if (saveToHistory) {
                    subtitleHistory.current.push({
                        subtitle: formatSub(subtitle),
                        mirroredGujaratiSubs: mirroredGujaratiSubs ? [...mirroredGujaratiSubs] : [],
                    });
                    limitHistorySize();
                }
                // Use selectedLngRef.current (not the closed-over selectedLng) so that any
                // stale callback still writes to the correct per-language key.
                writeSubtitleToStorage(newSubtitle, selectedLngRef.current);

                setSubtitleOriginal(formatSub(newSubtitle));
                window.dispatchEvent(new Event('storage'));

                // Only update mirrored timings if editing English
                if (selectedLngRef.current === 'English' && !skipUpdateMirrored) {
                    updateMirroredGujaratiTimings(newSubtitle);
                }
            }
        },
        [subtitle, setSubtitleOriginal, formatSub],
    );

    // This function will be called when selectedLng changes
    const handleLanguageChange = useCallback(async () => {
        // Get videoProps outside the handleLanguageChange function
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));

        // Only proceed if we have valid videoProps and selectedLng
        if (!videoProps || !videoProps.id || !selectedLng) return;

        console.log(`Language changed from ${previousLanguageRef.current} to: ${selectedLng}`);
        inMiddleofSessionEnding.current = true;

        // 1. First, end any existing tracking session
        if (sessionActive) {
            try {
                console.log(`Ending tracking session for previous language`);
                await EditTrackingService.endSession();
                setSessionActive(false);
            } catch (error) {
                console.error('Error ending tracking session:', error);
            }
        }

        // 2. Clean up session storage for ALL languages of this video
        if (videoProps && videoProps.id) {
            console.log('Session Storage', sessionStorage);
            Object.keys(sessionStorage).forEach((key) => {
                if (key.startsWith(`edit_session_${videoProps.id}_`) || key.startsWith(`read_only_${videoProps.id}_`)) {
                    endEditSession();
                }
            });
        }

        // 3. Wait a moment to ensure backend has processed the session end
        await new Promise((resolve) => setTimeout(resolve, 500));

        logCollectionService.logTaggedEvent(
            'subtitle_language_change',
            {
                from: previousLanguageRef.current,
                to: selectedLng,
                videoId: videoProps.id,
            },
            ['language-change', 'subtitle-editor'],
        );

        // 4. Configure dual column settings
        if (selectedLng === 'English' || selectedLng === 'Gujarati') {
            setShowDualColumns(false);
            // For single column languages, let the normal flow handle loading
        } else {
            setShowDualColumns(true);
            // if English-VO is available as a language, set that as left column
            if (videoProps.videoLanguages.some((lang) => lang.languageName === 'English-VO')) {
                if(selectedLng === 'English-VO'){
                    setLeftColumnLanguage('English');
                }else{
                    setLeftColumnLanguage('English-VO');
                }
            } else {
                setLeftColumnLanguage('English');
            }
            setRightColumnLanguage(selectedLng);

            // IMPORTANT: Don't load here, let the ToolBar handle it
            // We just set the dual column flags
        }

        // 5. Start a new session for the current language
        if (selectedLng) {
            console.log(`Starting new edit session for and resetting undo stack: ${selectedLng}`);
            subtitleHistory.current = [];
            startEditSession(videoProps.id, selectedLng);
        }

        // Mark that language change is complete
        inMiddleofSessionEnding.current = false;
    }, [
        selectedLng,
        sessionActive,
        startEditSession,
        setShowDualColumns,
        setLeftColumnLanguage,
        setRightColumnLanguage,
        endEditSession,
    ]);

    // Update the loadLeftColumnAsBase to be more explicit about when it should run
    const loadLeftColumnAsBase = useCallback(
        async (videoProps) => {
            // Only run this for dual column languages
            if (!showDualColumns) {
                console.log('Not dual columns, skipping loadLeftColumnAsBase');
                return;
            }

            try {
                console.log(`Loading ${leftColumnLanguage} as base for dual column view`);

                // Find the left column language index
                const leftColumnLangIndex = videoProps.videoLanguages.findIndex(
                    (lang) => lang.languageName === leftColumnLanguage,
                );

                if (leftColumnLangIndex === -1) {
                    console.warn(`Left column language ${leftColumnLanguage} not found`);
                    return;
                }

                // Fetch the left column language VTT data
                const leftColumnVttData = await fetchBlobUrl(videoProps.videoLanguages[leftColumnLangIndex].vttFileUrl);

                if (leftColumnVttData) {
                    // Parse the left column VTT data
                    const leftColumnSubtitles = getSubNodesfromVtt(leftColumnVttData);

                    // Set this as the base subtitle state
                    setSubtitle(
                        leftColumnSubtitles.map((item) => new Sub(item)),
                        false,
                    );

                    console.log(`Successfully loaded ${leftColumnLanguage} as base subtitle for dual column view`);
                    return leftColumnSubtitles; // Return for potential use in merging
                }
            } catch (error) {
                console.error('Error loading left column as base:', error);
            }
        },
        [leftColumnLanguage, setSubtitle, showDualColumns],
    );

    // Update mergeRightColumnIntoBase to accept baseSubtitles parameter
    const mergeRightColumnIntoBase = useCallback(
        async (videoProps, baseSubtitles = null) => {
            // Only run this for dual column languages
            if (!showDualColumns) {
                console.log('Not dual columns, skipping mergeRightColumnIntoBase');
                return;
            }

            try {
                console.log(`Merging ${rightColumnLanguage} into ${leftColumnLanguage} base`);

                // If no baseSubtitles provided, get from current subtitle state
                let leftColumnSubtitles = baseSubtitles;
                if (!leftColumnSubtitles) {
                    leftColumnSubtitles = subtitle.length > 0 ? subtitle : await loadLeftColumnAsBase(videoProps);
                }

                // Find the right column language index
                const rightColumnLangIndex = videoProps.videoLanguages.findIndex(
                    (lang) => lang.languageName === rightColumnLanguage,
                );

                if (rightColumnLangIndex === -1) {
                    console.warn(`Right column language ${rightColumnLanguage} not found`);
                    return;
                }

                // Fetch the right column language VTT data
                const rightColumnVttData = await fetchBlobUrl(
                    videoProps.videoLanguages[rightColumnLangIndex].vttFileUrl,
                );

                if (rightColumnVttData) {
                    // Parse the right column VTT data
                    const rightColumnSubtitles = getSubNodesfromVtt(rightColumnVttData);

                    // Merge right column data into the base subtitles.
                    // Only include entries where the right column (English-VO) actually has a cue;
                    // extra base cues beyond the right column count are skipped to avoid blank cues.
                    const mergedSubtitles = leftColumnSubtitles
                        .slice(0, rightColumnSubtitles.length)
                        .map((baseSub, index) => {
                        const rightSub = rightColumnSubtitles[index];

                        return new Sub({
                            // Use base subtitle timing and structure
                            ...baseSub,
                            // Add right column text as the main editing text
                            text: rightSub?.text || '',
                            text2: rightSub?.text2 || '',
                            // Keep left column text in E-prefixed fields for dual display
                            Etext: baseSub.text || '',
                            Etext2: baseSub.text2 || '',
                            Estart: baseSub.start,
                            Eend: baseSub.end,
                            // Preserve any other properties from right column
                            note: rightSub?.note || baseSub.note || '',
                            bookmark: rightSub?.bookmark || baseSub.bookmark || false,
                        });
                    });

                    // Update the subtitle state with merged data
                    setLanguageSubTitle(mergedSubtitles, false);

                    console.log(`Successfully merged ${rightColumnLanguage} into ${leftColumnLanguage} base`);
                }
            } catch (error) {
                console.error('Error merging right column into base:', error);
            }
        },
        [rightColumnLanguage, leftColumnLanguage, setLanguageSubTitle, showDualColumns, subtitle, loadLeftColumnAsBase],
    );

    // End the current session
    const cleanupSession = useCallback(async (languageOverride = null) => {
        // Get videoProps outside the handleLanguageChange function
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        const languageToClose = languageOverride || previousLanguageRef.current || selectedLng;

        // Only proceed if we have valid videoProps and selectedLng
        if (!videoProps || !videoProps.id || !languageToClose) return;

        try {
            // End the current session using the beacon API for reliability
            if (sessionActive) {
                await EditTrackingService.endSession();
                console.log(`Ended tracking session for: ${languageToClose}`);
            }

            // End the editing session
            await EditSessionService.endEditSessionWithBeacon(videoProps.id, languageToClose);
            cleanupSessionStorage(videoProps.id, languageToClose);
            console.log(`Ended edit session for: ${languageToClose}`);

            endEditSession();
        } catch (error) {
            console.error('Error in cleanup:', error);
        }
    }, [selectedLng, sessionActive, cleanupSessionStorage, endEditSession]);

    // Replace the problematic useEffect that manages language changes (around line 665)
    // Start edit session when component loads or language changes
    useEffect(() => {
        // Get videoProps outside the handleLanguageChange function
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));

        // Only proceed if we have valid videoProps and selectedLng
        if (!videoProps || !videoProps.id || !selectedLng) return;

        // Skip if language hasn't actually changed
        if (previousLanguageRef.current === selectedLng) {
            //console.log(`Language unchanged: ${selectedLng} - skipping session change`);
            return;
        }

        if (inMiddleofSessionEnding.current) {
            console.log(`Session is in the process of ending for language: ${selectedLng}`);
            return;
        }

        // Execute the language change handler
        handleLanguageChange();

        // Clean up function when component unmounts or language changes
        return () => {
            console.log(`Cleaning up edit session for: ${previousLanguageRef.current}`);

            // Clear any intervals
            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
                keepAliveIntervalRef.current = null;
            }
            const closingLanguage = previousLanguageRef.current || selectedLng;
            // Execute cleanup
            cleanupSession(closingLanguage);
            // Update previous language reference
            previousLanguageRef.current = selectedLng;
            inMiddleofSessionEnding.current = false;
        };
    }, [selectedLng, sessionActive, startEditSession, cleanupAllSessions]);

    // Update the window unload handler
    useEffect(() => {
        const handleUnload = () => {
            console.log('Handling page unload');
            // Synchronous cleanup
            cleanupBeforeUnload();
            // Use the beacon API
            endEditSession(true);
        };

        window.addEventListener('beforeunload', handleUnload);

        return () => {
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, []); // Empty dependency array

    const cleanupBeforeUnload = () => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        if (videoProps?.id && selectedLng) {
            const sessionKey = generateSessionKey(videoProps.id, selectedLng);
            const readOnlyKey = generateReadOnlyKey(videoProps.id, selectedLng);
            sessionStorage.removeItem(sessionKey);
            sessionStorage.removeItem(readOnlyKey);

            // Clear interval
            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
                keepAliveIntervalRef.current = null;
            }
        }
    };

    const copySubs = useCallback(() => formatSub(subtitle), [subtitle, formatSub]);

    // Add a function to limit history size
    const limitHistorySize = useCallback(() => {
        // Hard limit on history size to prevent unbounded growth
        const MAX_HISTORY_SIZE = 50;

        if (subtitleHistory.current.length > MAX_HISTORY_SIZE) {
            // Remove older entries
            subtitleHistory.current = subtitleHistory.current.slice(-MAX_HISTORY_SIZE);
        }
    }, []);

    const undoSubs = useCallback(() => {
        if (subtitleHistory.current.length === 0) return;
        const last = subtitleHistory.current.pop();
        if (last && last.subtitle && Array.isArray(last.subtitle) && last.subtitle.length > 0) {
            if (last.mirroredGujaratiSubs && last.mirroredGujaratiSubs.length === last.subtitle.length) {
                setMirroredGujaratiSubs(last.mirroredGujaratiSubs || []);
            }
            // Always use formatSub to ensure correct structure
            setSubtitle(formatSub(last.subtitle), false, true);
        }
    }, [setSubtitle, setMirroredGujaratiSubs, subtitleHistory]);

    const clearSubs = useCallback(() => {
        setSubtitle([]);
        subtitleHistory.current.length = 0;
    }, [setSubtitle, subtitleHistory]);

    const checkSub = useCallback(
        (sub) => {
            const s = sub instanceof Sub ? sub : new Sub(sub);

            const index = hasSub(s);
            if (index < 0) return;
            const previous = subtitle[index - 1];

            // Special Case for the language subtitles where start and end are at 0
            if (s.startTime === 0 && s.endTime === 0) {
                return false; // return false if the start and end are at 0 - To stop highlighting the subtitle
            }
            return (previous && s.startTime < previous.endTime) || !s.check || s.duration < 0.002;
        },
        [subtitle, hasSub],
    );

    const notify = useCallback(
        (obj) => {
            // https://github.com/igorprado/react-notification-system
            const notification = notificationSystem.current;
            notification.clearNotifications();
            notification.addNotification({
                position: 'tc',
                dismissible: 'none',
                autoDismiss: 2,
                message: obj.message,
                level: obj.level,
            });
        },
        [notificationSystem],
    );

    const removeSub = useCallback(
        (sub) => {
            const index = hasSub(sub);
            if (index < 0) return;
            const subs = copySubs();
            subs.splice(index, 1);
            setSubtitle(subs);

            EditTrackingService.trackEdit(index, 'delete', 'subtitle', sub?.text2 || sub?.text || '', '');

            if (selectedLng === 'English') {
                // Remove the corresponding mirrored Gujarati sub and shift text fields down
                setMirroredGujaratiSubs((prevSubs) => {
                    if (!prevSubs || prevSubs.length === 0 || index < 0 || index >= prevSubs.length) return prevSubs;

                    // Remove the sub at index
                    const newSubs = prevSubs.slice(0, index).concat(prevSubs.slice(index + 1));

                    // Shift text fields down for all subs after the removed one
                    for (let i = index; i < newSubs.length; i++) {
                        newSubs[i] = {
                            ...newSubs[i],
                            text: prevSubs[i + 1]?.text ?? '',
                            text2: prevSubs[i + 1]?.text2 ?? '',
                            note: prevSubs[i + 1]?.note ?? '',
                            bookmark: prevSubs[i + 1]?.bookmark ?? false,
                        };
                    }

                    return newSubs;
                });
            }
        },
        [hasSub, copySubs, setSubtitle, selectedLng, setMirroredGujaratiSubs],
    );

    const addSub = useCallback(
        (index, sub) => {
            const subs = copySubs();
            subs.splice(index, 0, formatSub(sub));
            setSubtitle(subs);

            EditTrackingService.trackEdit(index, 'add', 'subtitle', '', sub?.text2 || sub?.text || '');

            if (selectedLng === 'English') {
                addMirroredGujaratiSub(index, sub);
            }
        },
        [copySubs, setSubtitle, formatSub, selectedLng, addMirroredGujaratiSub],
    );

    const updateSub = useCallback(
        (sub, obj) => {
            const index = hasSub(sub);
            if (index < 0) return;
            const subs = copySubs();
            const subClone = formatSub(sub);
            Object.assign(subClone, obj);
            subs[index] = subClone;
            setSubtitle(subs);
        },
        [hasSub, copySubs, setSubtitle, formatSub],
    );

    // Add this function after the updateSub definition (around line 823)
    const updateSubWithTracking = useCallback(
        (sub, obj) => {
            // Find the subtitle index
            const index = hasSub(sub);
            if (index < 0) return;

            // Call the original updateSub function immediately (for smooth UI)
            updateSub(sub, obj);

            // Create a debounced tracking function that only fires after typing stops
            // This prevents tracking every keystroke - it waits 300ms after last change
            const trackingKey = `${index}-text-${obj.text2 !== undefined ? 'text2' : 'text'}`;
            
            if (!updateSubWithTracking.debouncedTracking) {
                updateSubWithTracking.debouncedTracking = {};
            }

            if (!updateSubWithTracking.debouncedTracking[trackingKey]) {
                updateSubWithTracking.debouncedTracking[trackingKey] = debounce(
                    (idx, field, oldVal, newVal) => {
                        EditTrackingService.trackEdit(idx, 'text', field, oldVal, newVal);
                    },
                    300 // Wait 300ms after user stops typing before tracking
                );
            }

            // Track text changes with debouncing (only after user stops typing)
            if (obj.text2 !== undefined && obj.text2 !== sub.text2) {
                updateSubWithTracking.debouncedTracking[trackingKey](index, 'text2', sub.text2, obj.text2);
            } else if (obj.text !== undefined && obj.text !== sub.text) {
                updateSubWithTracking.debouncedTracking[trackingKey](index, 'text', sub.text, obj.text);
            }

            // Track timing changes immediately (these are not rapid keystroke events)
            if (obj.start !== undefined && obj.start !== sub.start) {
                EditTrackingService.trackEdit(index, 'timing', 'start', sub.start, obj.start);
            }

            if (obj.end !== undefined && obj.end !== sub.end) {
                EditTrackingService.trackEdit(index, 'timing', 'end', sub.end, obj.end);
            }
        },
        [hasSub, updateSub],
    );

    const updateSubIndex = useCallback(
        (sub, obj, index) => {
            const subs = copySubs();
            const subClone = subs[index];
            Object.assign(subClone, obj);
            if (subClone.check) {
                subs[index] = subClone;
                setSubtitle(subs);
            }
        },
        [copySubs, setSubtitle],
    );

    const mergeSub = useCallback(
        (sub) => {
            const index = hasSub(sub);
            if (index < 0) return;
            const subs = copySubs();
            const next = subs[index + 1];
            if (!next) return;
            let merge;
            if (!sub.text2) {
                merge = newSub({
                    start: sub.start,
                    end: next.end,
                    text: sub.text.trim() + '\n' + next.text.trim(),
                    Estart: sub.Estart,
                    Eend: next.Eend,
                    Etext:
                        sub.Etext !== undefined && next.Etext !== undefined
                            ? sub.Etext.trim() + '\n' + next.Etext.trim()
                            : '',
                });
            } else {
                merge = newSub({
                    start: sub.start,
                    end: next.end,
                    text: sub.text.trim() + '\n' + next.text.trim(),
                    text2: sub.text2.trim() + '\n' + next.text2.trim(),
                    Estart: sub.Estart,
                    Eend: next.Eend,
                    Etext:
                        sub.Etext !== undefined && next.Etext !== undefined
                            ? sub.Etext.trim() + '\n' + next.Etext.trim()
                            : '',
                    Etext2:
                        sub.Etext2 !== undefined && next.Etext2 !== undefined
                            ? sub.Etext2.trim() + '\n' + next.Etext2.trim()
                            : '',
                });
            }
            subs[index] = merge;
            subs.splice(index + 1, 1);
            setSubtitle(subs);

            EditTrackingService.trackEdit(
                index,
                'merge',
                'subtitle',
                `${sub?.text2 || sub?.text || ''}\n${next?.text2 || next?.text || ''}`,
                merge?.text2 || merge?.text || '',
            );

            // Merge Mirrored Gujarati
            setMirroredGujaratiSubs((prevGujaratiSubs) => {
                if (!prevGujaratiSubs || prevGujaratiSubs.length <= index + 1) return prevGujaratiSubs;
                const mergedGujarati = {
                    ...prevGujaratiSubs[index],
                    start: prevGujaratiSubs[index].start,
                    end: prevGujaratiSubs[index + 1].end,
                    text:
                        (prevGujaratiSubs[index].text || '').trim() +
                        '\n' +
                        (prevGujaratiSubs[index + 1].text || '').trim(),
                    text2:
                        (prevGujaratiSubs[index].text2 || '').trim() +
                        '\n' +
                        (prevGujaratiSubs[index + 1].text2 || '').trim(),
                    note: '', // Optionally merge notes if needed
                    bookmark: false, // Optionally merge bookmarks if needed
                };
                const newGujaratiSubs = [...prevGujaratiSubs];
                newGujaratiSubs[index] = mergedGujarati;
                newGujaratiSubs.splice(index + 1, 1);
                return newGujaratiSubs;
            });
        },
        [hasSub, copySubs, setSubtitle, newSub, setMirroredGujaratiSubs],
    );

    const onKeyDown = useCallback(
        (event) => {
            if (event.ctrlKey && event.key === 'z') {
                console.log('Control key and Z pressed');
                undoSubs(); // Call the undoSubs function
                event.preventDefault(); // Prevents the default action
            }

            const keyCode = getKeyCode(event);

            const KEY_TAB = 9;
            const KEY_RIGHT_ARROW = 39;
            const KEY_LEFT_ARROW = 37;
            const KEY_Z = 90;
            //console.log(`Key pressed: ${keyCode} - ${event.key}`);
            switch (keyCode) {
                case KEY_TAB:
                    event.preventDefault();
                    if (player) {
                        if (player.element.paused) {
                            console.log(`Toggling pause - KEY_TAB: ${player.element.currentTime}`);
                            player.element.play();
                        } else {
                            console.log(`Toggling play - KEY_TAB: ${player.element.currentTime}`);
                            player.element.pause();
                        }
                    }
                    break;

                case KEY_Z:
                    if (event.metaKey || event.ctrlKey) {
                        event.preventDefault(); // Only prevent default when modifier key is pressed
                        undoSubs();
                    }
                    break;

                default:
                    break;
            }
        },
        [player, playing, undoSubs],
    );

    useMemo(() => {
        const currentSubIndex = subtitle.findIndex(
            (item) => item.startTime <= currentTime && item.endTime > currentTime,
        );

        // If no subtitle is active at the current time (gap)
        if (currentSubIndex === -1) {
            // Find the last subtitle that appeared before the current time
            const lastSubtitleBeforeGap = subtitle.reduce((closest, item, index) => {
                if (item.endTime <= currentTime && (closest === -1 || item.endTime > subtitle[closest].endTime)) {
                    return index;
                }
                return closest;
            }, -1);

            // Only update if we found a valid previous subtitle
            if (lastSubtitleBeforeGap !== -1) {
                setCurrentIndex(lastSubtitleBeforeGap);
            }
            //console.log(`No subtitle at current time ${currentTime}, set to last subtitle index: ${lastSubtitleBeforeGap}`);
        } else {
            // Normal case - subtitle exists at current time
            setCurrentIndex(currentSubIndex);
            //console.log(`Current subtitle index updated: ${currentSubIndex} at time ${currentTime}`);
        }
    }, [currentTime, subtitle]);

    const getLanguageVTTFile = async (language) => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        if (!videoProps || !videoProps.videoLanguages) return '';

        // Find the index for the specified language
        const languageIndex = videoProps.videoLanguages.findIndex(
            (lang) => lang.languageName && lang.languageName === language,
        );

        if (languageIndex === -1) {
            console.warn(`${language} language not found in videoProps`);
            return '';
        }

        const languageVttUrl = videoProps.videoLanguages[languageIndex].vttFileUrl;
        if (!languageVttUrl) {
            console.warn(`${language} VTT URL not found`);
            return '';
        }

        return languageVttUrl;
    };

    const fetchData = async (selectedLangIndex) => {
        let subtitleData = '';
        let videoProps = JSON.parse(localStorage.getItem('videoProps'));

        try {
            const vttData = await fetchBlobUrl(videoProps.videoLanguages[selectedLangIndex].vttFileUrl).catch(
                (error) => {
                    console.error('SubtitleEditor.js Error fetching VTT file:', error);
                },
            );

            if (vttData) {
                // once the data is fetched, set the subtitleString in the videoProps
                videoProps.videoLanguages[selectedLangIndex].subtitleString = vttData;
                subtitleData = vttData;
                window.localStorage.setItem('videoProps', JSON.stringify(videoProps));
            }
        } catch (error) {
            console.log('SubtitleEditor.js  Error fetching VTT file:', error);
        }

        return subtitleData;
    };

    const onLoad = useCallback(() => {
        // Read the currently active language from the ref so this works correctly
        // for any language including English-VO (which has its own VTT and index).
        const activeLng = selectedLngRef.current || 'English';
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));

        // Resolve the actual index of activeLng in videoLanguages so we fetch the
        // correct VTT. Falling back to LanguageList.ENGLISH (0) was wrong for any
        // non-English language and caused English subtitles to overwrite the display.
        const selectedLangIndex = videoProps.videoLanguages.findIndex(
            (lang) => lang.languageName === activeLng,
        );
        const resolvedIndex = selectedLangIndex >= 0 ? selectedLangIndex : LanguageList.ENGLISH;

        // Show cached data immediately for a fast initial render…
        const localSubtitle = readSubtitleFromStorage(activeLng);
        if (localSubtitle && localSubtitle.length) {
            setSubtitle(localSubtitle.map((item) => new Sub(item)), false);
        }

        // …but ALWAYS follow up with the server fetch so that any previously
        // corrupted localStorage entry is replaced by the authoritative server copy.
        if (videoProps.videoLanguages[resolvedIndex].metadataCheckFlag.length > 0) {
            setBookmarked(videoProps.videoLanguages[resolvedIndex].metadataCheckFlag);
        }

        if (videoProps.videoLanguages[resolvedIndex].metadataComments.length > 0) {
            setSubtitleComment(videoProps.videoLanguages[resolvedIndex].metadataComments);
        }

        // Wait for the fetchData to complete before parsing the subtitleData
        fetchData(resolvedIndex).then((subtitleData) => {
            if (subtitleData !== null) {
                const stateUpdated = getSubNodesfromVtt(subtitleData);
                if (stateUpdated && stateUpdated.length > 0) {
                    // Server copy is authoritative — apply it and write back to cache.
                    setSubtitle(stateUpdated.map((item) => new Sub(item)), false);
                }
            }
        });
    }, []);

    // Run onLoad exactly once on mount — separate from the event-listener effect below
    // so that onLoad() is NOT re-called every time onKeyDown changes (which happens on
    // every subtitle edit because onKeyDown → undoSubs → setSubtitle → subtitle dep chain).
    useEffect(() => {
        onLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep keyboard and custom-load event listeners up-to-date when handlers change,
    // but do NOT call onLoad() here — that belongs in the mount-only effect above.
    useEffect(() => {
        const keyDownHandler = onKeyDown;
        const loadHandler = onLoad;

        window.addEventListener('keydown', keyDownHandler);
        window.addEventListener('onLoad', loadHandler);

        return () => {
            window.removeEventListener('keydown', keyDownHandler);
            window.removeEventListener('onLoad', loadHandler);
        };
    }, [onKeyDown, onLoad]);

    // Memoize the search functions
    const processSearchMatches = useCallback(
        (term) => {
            if (!term || !subtitle) {
                setMatchOffsets([]);
                setCurrentMatchIndex(0);
                return;
            }

            const matches = [];
            const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(escapedTerm, 'gi');

            subtitle.forEach((item, index) => {
                if (!item.text2) return;

                let match;
                while ((match = regex.exec(item.text2)) !== null) {
                    matches.push({
                        textIndex: index,
                        subtitleIndex: index,
                        start: match.index,
                        end: match.index + match[0].length,
                    });
                }
            });

            setMatchOffsets(matches);
            if (matches.length > 0) {
                setCurrentMatchIndex(0);
            }
        },
        [subtitle],
    );

    // Optimize focus match function
    const focusMatchInTextarea = useCallback((matchIndex, startPos, endPos) => {
        const textareaId = `subtitleTextInput${matchIndex}`;
        const textarea = document.getElementById(textareaId);

        if (textarea) {
            // Scroll the parent container to make the textarea visible
            textarea.parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Focus the textarea
            textarea.focus();

            // Select the matched text
            textarea.setSelectionRange(startPos, endPos);
        }
    }, []);

    // Optimize go to next/previous match functions
    const goToNextMatch = useCallback(() => {
        if (matchOffsets.length > 0) {
            const nextIndex = (currentMatchIndex + 1) % matchOffsets.length;
            setCurrentMatchIndex(nextIndex);

            const match = matchOffsets[nextIndex];
            if (match) {
                focusMatchInTextarea(match.subtitleIndex, match.start, match.end);
            }
        }
    }, [matchOffsets, currentMatchIndex, focusMatchInTextarea]);

    const goToPreviousMatch = useCallback(() => {
        if (matchOffsets.length > 0) {
            const prevIndex = (currentMatchIndex - 1 + matchOffsets.length) % matchOffsets.length;
            setCurrentMatchIndex(prevIndex);

            const match = matchOffsets[prevIndex];
            if (match) {
                focusMatchInTextarea(match.subtitleIndex, match.start, match.end);
            }
        }
    }, [matchOffsets, currentMatchIndex, focusMatchInTextarea]);

    // Monitor search term changes efficiently
    useEffect(() => {
        // Debounce search term processing
        const timer = setTimeout(() => {
            processSearchMatches(searchTerm);
        }, 300);

        return () => {
            clearTimeout(timer);
        };
    }, [searchTerm, processSearchMatches]);

    // Focus on first match when matches change
    useEffect(() => {
        if (matchOffsets.length > 0 && currentMatchIndex >= 0) {
            const match = matchOffsets[currentMatchIndex];
            if (match) {
                setTimeout(() => {
                    focusMatchInTextarea(match.subtitleIndex, match.start, match.end);
                }, 100);
            }
        }
    }, [matchOffsets, currentMatchIndex, focusMatchInTextarea]);

    // Helper functions for search
    const handleShowSearch = () => {
        setShowSearch((prev) => !prev);
    };

    const onSearchTermChange = (term) => {
        setSearchTerm(term);
        // Process search matches
        processSearchMatches(term);
    };

    const onReplaceTermChange = (term) => {
        setReplaceTerm(term);
    };

    const handleReplace = useCallback(() => {
        if (!searchTerm || matchOffsets.length === 0) return;

        const { textIndex, start, end } = matchOffsets[currentMatchIndex];
        const newSubtitles = [...subtitle];
        const newText =
            newSubtitles[textIndex].text2.slice(0, start) + replaceTerm + newSubtitles[textIndex].text2.slice(end);

        newSubtitles[textIndex] = {
            ...newSubtitles[textIndex],
            text2: newText,
        };

        setSubtitle(newSubtitles);

        // Recalculate matches after replacing
        setTimeout(() => {
            processSearchMatches(searchTerm);
        }, 0);
    }, [searchTerm, replaceTerm, matchOffsets, currentMatchIndex, subtitle, setSubtitle, processSearchMatches]);

    const handleReplaceAll = useCallback(() => {
        if (!searchTerm) return;

        const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedTerm, 'gi');

        const newSubtitles = subtitle.map((item) => {
            if (!item.text2) return item;

            return {
                ...item,
                text2: item.text2.replace(regex, replaceTerm),
            };
        });

        setSubtitle(newSubtitles);
        setMatchOffsets([]);
        setCurrentMatchIndex(0);
    }, [searchTerm, replaceTerm, subtitle, setSubtitle]);

    const handleSetTime = (timeInSeconds) => {
        if (player && player.updateTime) {
            player.updateTime(timeInSeconds);
        } else if (player && player.element) {
            player.element.currentTime = timeInSeconds;
        }
    };

    // Function to determine user level based on role
    const determineUserLevel = useCallback((videoProps, selectedLng) => {
        if (!videoProps || !selectedLng) return '';

        // Find the selected language in videoProps
        const selectedLanguage = videoProps.videoLanguages.find((lang) => lang.languageName === selectedLng);

        if (!selectedLanguage) return 'Level 1'; // Default if language not found

        // Determine level based on the subtitle progress state
        switch (selectedLanguage.processStage) {
            case 'LEVEL0_AI_COMPLETE':
                return 'Level 1'; // First level editing AI-generated subtitles
            case 'LEVEL2_WIP':
            case 'LEVEL1_SEVAK_COMPLETE':
                return 'Level 2'; // Second level editing after Level 1 is complete
            // The next level to work on: after L2 is submitted, and once Published, that's Level 3
            case 'LEVEL2_SEVAK_COMPLETE':
            case 'LEVEL3_WIP':
            case 'LEVEL3_SEVAK_COMPLETE':
            case 'PUBLISHED':
                return 'Level 3';
            default:
                return 'Level 1'; // Default level
        }
    }, []);

    // Start tracking session when component mounts
    const sessionStartingToTrackRef = useRef(false);
    useEffect(() => {
        const startTracking = async () => {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));

            if (videoProps && selectedLng) {
                const level = determineUserLevel(videoProps, selectedLng);
                setUserLevel(level);

                const session = await EditTrackingService.startSession(videoProps.id, selectedLng, level, 'V1');

                if (session) {
                    setSessionActive(true);
                    sessionStartingToTrackRef.current = false;
                    console.log(`Tracking session started for language: ${selectedLng} with level: ${level}`);
                } else {
                    sessionStartingToTrackRef.current = false;
                }
            }
        };

        if (selectedLng && !sessionStartingToTrackRef.current) {
            sessionStartingToTrackRef.current = true;
            console.log(`Starting tracking session for language: ${selectedLng}`);
            startTracking();
        }

        return () => {
            EditTrackingService.endSession();
            sessionStartingToTrackRef.current = false;
        };
    }, [selectedLng, determineUserLevel]);

    const props = {
        selectedLng,
        setSelectedLng,
        lngIndex,
        setLngIndex,

        // Add these new props
        leftColumnLanguage,
        setLeftColumnLanguage,
        rightColumnLanguage,
        setRightColumnLanguage,
        showDualColumns,
        setShowDualColumns,

        showSearch,
        setShowSearch,
        handleShowSearch,

        player,
        setPlayer,
        subtitle,
        setSubtitle,
        waveform,
        setWaveform,
        currentTime,
        setCurrentTime,
        currentIndex,
        setCurrentIndex,
        playing,
        setPlaying,
        language,
        setLanguage,
        loading,
        setLoading,
        setProcessing,
        subtitleHistory,
        translate,
        setTranslate,
        directUrl,
        setDirectUrl,
        viewEng,
        setViewEng,
        bookmarked,
        setBookmarked,
        subtitleComment,
        setSubtitleComment,

        notify,
        newSub,
        hasSub,
        checkSub,
        removeSub,
        addSub,
        undoSubs,
        clearSubs,
        updateSub: updateSubWithTracking,
        updateSubIndex,
        formatSub,
        mergeSub,
        setLanguageSubTitle,
        readOnly,
        duplicateSessionDetected,
        handleSetTime,
        userLevel, // Pass user level to components
        saveMirroredGujaratiVtt,
        splitMirroredGujratiSubtitle,
    };

    // Use memoization for expensive processing
    const processedSubtitles = useMemo(() => {
        return subtitle.map((sub) => ({
            ...sub,
            // Add any additional processing here
        }));
    }, [subtitle]);

    // Define this function with useCallback
    const highlightSearchTerm = useCallback(
        (text, searchTerm, index) => {
            if (!text || !searchTerm) return text || '';

            try {
                const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');

                // Replace matching text with highlighted mark tags
                const highlightedText = text.replace(regex, (match, group, offset) => {
                    // Safely check if this is the current match
                    const isCurrentMatch = (matchOffsets || []).some(
                        (m) =>
                            m &&
                            m.textIndex === index &&
                            m.start <= offset &&
                            offset < m.end &&
                            currentMatchIndex === (matchOffsets || []).indexOf(m),
                    );

                    // Use different class for current match vs other matches
                    return isCurrentMatch ? `<mark class="current">${match}</mark>` : `<mark>${match}</mark>`;
                });

                return highlightedText;
            } catch (error) {
                console.error('Error highlighting text:', error);
                return text;
            }
        },
        [matchOffsets, currentMatchIndex],
    );

    return (
        <>
            <GlobalStyle />
            <Style onMouseMove={handleUserActivity} onClick={handleUserActivity} onKeyDown={handleUserActivity}>
                {Date.now() - lastActivity > INACTIVITY_TIMEOUT - 60000 && (
                    <div className="inactivity-warning-banner">
                        <Alert severity="warning">
                            Your session will expire in less than 1 minute due to inactivity. Move your mouse or press a
                            key to continue editing.
                        </Alert>
                    </div>
                )}

                {readOnly && (
                    <div className="read-only-banner">
                        <Alert
                            severity="error"
                            icon={<LockIcon fontSize="large" />}
                            sx={{
                                fontSize: '1.1rem',
                                fontWeight: 700,
                                alignItems: 'center',
                                py: 1.5,
                                '& .MuiAlert-icon': { fontSize: '2rem' },
                            }}
                        >
                            {activeEditor && activeEditor.startsWith('The editing session')
                                ? `READ-ONLY MODE: ${activeEditor}`
                                : `READ-ONLY MODE: This subtitle is currently being edited by ${activeEditor}. You can view but cannot make changes.`}
                        </Alert>
                    </div>
                )}

                <div className="toolbar-container">
                    <Header
                        {...props}
                        showSearch={showSearch}
                        handleShowSearch={handleShowSearch}
                        searchTerm={searchTerm}
                        replaceTerm={replaceTerm}
                        onSearchTermChange={onSearchTermChange}
                        onReplaceTermChange={onReplaceTermChange}
                        matchOffsets={matchOffsets}
                        currentMatchIndex={currentMatchIndex}
                        goToNextMatch={goToNextMatch}
                        goToPreviousMatch={goToPreviousMatch}
                        handleReplace={handleReplace}
                        handleReplaceAll={handleReplaceAll}
                    />
                </div>

                <div className="container">
                    <div className="player-container">
                        <Player {...props} />
                    </div>

                    <div className="subtitles-container">
                        <Subtitles
                            {...props}
                            searchTerm={searchTerm}
                            matchOffsets={matchOffsets || []}
                            currentMatchIndex={currentMatchIndex}
                            highlightSearchTerm={highlightSearchTerm}
                        />
                    </div>
                </div>

                <Footer {...props} />

                {loading ? <Loading loading={loading} /> : null}
                {processing > 0 && processing < 100 ? <ProgressBar processing={processing} /> : null}
                <NotificationSystem ref={notificationSystem} allowHTML={true} />
            </Style>
        </>
    );
}
